import React from 'react'
import { Link } from 'react-router-dom'
import useSWRMutation from 'swr/mutation'
import { useSWRConfig } from 'swr'

import { useAuth } from '../../context/auth'
import { useAlerts } from '../../context/alerts'
import { formatDateShort } from '../../lib/dates'
import { Invoice, InvoiceStatus } from '../../types'
import List from '../ui/list'
import ActivityBadge from './activityBadge'
import Amount from '../ui/currency/amount'
import Button, { ButtonSize, ButtonVariant } from '../ui/button'
import { getInvoiceById, saveOrUpdateInvoice } from '../../api'
import { SUMMARY_REVALIDATE_DELAY_MS } from '../../lib/summaryConstants'
import { revalidateClientCaches } from '../../lib/revalidateClientCaches'
import {
  applyPaidToInvoiceSummaryOptimistic,
  revalidateInvoiceSummaryCaches,
} from '../../lib/revalidateInvoiceSummaryCaches'
import { publishPaidSummaryAnimation } from '../../lib/summaryAnimationBus'
import useScreenSize from '../../context/screenSize'
import InvoiceCard from './Card'
import StatusBadge from './statusBadge'
import InvoiceActionsDropdown from './invoiceActionsDropdown'
import { ReactComponent as CheckIcon } from '../../static/images/check.svg'

import './styles.scss'

const ROW_FADE_MS = 400
const ROW_HIDE_DELAY_MS = 2000

export type PaidOverride = { invoice: Invoice; sortIndex: number }

export function buildDisplayInvoices(
  invoices: Invoice[],
  paidOverrides: Map<string, PaidOverride>,
): Invoice[] {
  const serverIds = new Set(invoices.map((inv) => inv._id).filter(Boolean))
  const merged = invoices.map((inv) => {
    const override = inv._id ? paidOverrides.get(inv._id) : undefined
    return override?.invoice ?? inv
  })

  const orphans: PaidOverride[] = []
  paidOverrides.forEach((entry, id) => {
    if (!serverIds.has(id)) {
      orphans.push(entry)
    }
  })
  orphans.sort((a, b) => a.sortIndex - b.sortIndex)

  for (let i = 0; i < orphans.length; i++) {
    const { invoice, sortIndex } = orphans[i]
    merged.splice(Math.min(sortIndex, merged.length), 0, invoice)
  }

  return merged
}

export type IssueDateSort = 'asc' | 'desc'

export function sortByIssueDate(invoices: Invoice[], direction: IssueDateSort): Invoice[] {
  const sign = direction === 'asc' ? 1 : -1
  return [...invoices].sort((a, b) => {
    const aTime = new Date(a.issueDate).getTime()
    const bTime = new Date(b.issueDate).getTime()
    const aVal = Number.isNaN(aTime) ? 0 : aTime
    const bVal = Number.isNaN(bTime) ? 0 : bTime
    return (aVal - bVal) * sign
  })
}

/**
 * Shows a list of invoices.
 * Looks like a table with the following columns:
 * Issue Date,
 * Code,
 * Client,
 * Amount,
 * Status: Open, Paid, Overdue
 * Activity: Draft, Email Sent, Client Viewed
 * @returns
 */
type Props = {
  invoices: Array<Invoice>
  refreshInvoices: () => void
  statusFilter: string[]
  issueDateSort: IssueDateSort
  onIssueDateSortChange: (sort: IssueDateSort) => void
}
const InvoiceList: React.FC<Props> = ({
  invoices,
  refreshInvoices,
  statusFilter,
  issueDateSort,
  onIssueDateSortChange,
}) => {
  const { user } = useAuth()
  const { isMobile } = useScreenSize()
  const { addAlert } = useAlerts()
  const { mutate: mutateCache } = useSWRConfig()
  const { trigger: updateInvoice } = useSWRMutation('/api/invoices', saveOrUpdateInvoice)

  const [updatingInvoiceId, setUpdatingInvoiceId] = React.useState<string | null>(null)
  const [paidOverrides, setPaidOverrides] = React.useState<Map<string, PaidOverride>>(new Map())
  const [removingInvoiceIds, setRemovingInvoiceIds] = React.useState<Set<string>>(new Set())
  const removalTimersRef = React.useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  const showPaidInFilter = statusFilter.includes(InvoiceStatus.PAID)

  React.useEffect(() => {
    if (paidOverrides.size === 0) return
    setPaidOverrides((prev) => {
      let changed = false
      const next = new Map(prev)
      prev.forEach((_override, id) => {
        const serverInvoice = invoices.find((inv) => inv._id === id)
        if (showPaidInFilter && serverInvoice?.status === InvoiceStatus.PAID) {
          next.delete(id)
          changed = true
        }
      })
      return changed ? next : prev
    })
  }, [invoices, showPaidInFilter, paidOverrides.size])

  React.useEffect(() => {
    const timers = removalTimersRef.current
    return () => {
      timers.forEach((timer) => clearTimeout(timer))
      timers.clear()
    }
  }, [])

  const scheduleRowRemoval = React.useCallback((invoiceId: string) => {
    const existing = removalTimersRef.current.get(invoiceId)
    if (existing) clearTimeout(existing)

    const fadeTimer = setTimeout(() => {
      setRemovingInvoiceIds((prev) => new Set(prev).add(invoiceId))
      const cleanupTimer = setTimeout(() => {
        setPaidOverrides((prev) => {
          const next = new Map(prev)
          next.delete(invoiceId)
          return next
        })
        setRemovingInvoiceIds((prev) => {
          const next = new Set(prev)
          next.delete(invoiceId)
          return next
        })
        removalTimersRef.current.delete(invoiceId)
      }, ROW_FADE_MS)
      removalTimersRef.current.set(invoiceId, cleanupTimer)
    }, ROW_HIDE_DELAY_MS)

    removalTimersRef.current.set(invoiceId, fadeTimer)
  }, [])

  const handlePaid = async (invoice: Invoice) => {
    const id = invoice._id
    if (!id || id.toString().trim() === '') return
    if (window.confirm(`Are you sure you want to set as PAID invoice #${invoice.code}`) !== true) return

    const sortIndex = buildDisplayInvoices(invoices, paidOverrides).findIndex((inv) => inv._id === id)

    setUpdatingInvoiceId(id)
    try {
      const res = await updateInvoice({ body: { _id: id, status: InvoiceStatus.PAID } })
      const paidInvoice: Invoice = {
        ...invoice,
        ...(res?.data ?? {}),
        status: InvoiceStatus.PAID,
      }
      setPaidOverrides((prev) =>
        new Map(prev).set(id, { invoice: paidInvoice, sortIndex: Math.max(sortIndex, 0) }),
      )
      const invoiceTotal = Number(invoice.total) || 0
      publishPaidSummaryAnimation({
        paidAmountDelta: invoiceTotal,
        paidCountDelta: 1,
        dueAmountDelta: -invoiceTotal,
        dueCountDelta: -1,
      })
      await applyPaidToInvoiceSummaryOptimistic(mutateCache, invoiceTotal)
      await mutateCache(
        `/api/invoices/${id}`,
        () => getInvoiceById(`/api/invoices/${id}`),
        { revalidate: false },
      )
      window.setTimeout(() => {
        void revalidateInvoiceSummaryCaches(mutateCache)
        if (invoice.client?._id || invoice.clientId) {
          void revalidateClientCaches(mutateCache)
        }
      }, SUMMARY_REVALIDATE_DELAY_MS)
      addAlert({
        type: 'info',
        message: `Invoice ${invoice.code} has been marked as paid`,
      })
      refreshInvoices()
      if (!showPaidInFilter) {
        scheduleRowRemoval(id)
      }
    } catch {
      addAlert({ type: 'error', message: 'Could not mark invoice as paid' })
    } finally {
      setUpdatingInvoiceId(null)
    }
  }

  const displayInvoices = sortByIssueDate(
    buildDisplayInvoices(invoices, paidOverrides),
    issueDateSort,
  )

  const headers = [
    {
      label: (
        <button
          type='button'
          className={`invoice-list-sort-header ${issueDateSort === 'desc' ? 'sort-descending' : 'sort-ascending'}`}
          onClick={() => onIssueDateSortChange(issueDateSort === 'asc' ? 'desc' : 'asc')}
        >
          <h6>Issue Date</h6>
          <span className='invoice-list-sort-header__icon' aria-hidden />
        </button>
      ),
    },
    { label: <h6># Code</h6> },
    { label: <h6>Client</h6> },
    { label: <h6>Total</h6> },
    { label: <h6 className='text-center'>Status</h6> },
    { label: <h6 className='text-center fs-7 d-none d-sm-block'>Activity</h6> },
    { label: <h6 className='text-center fs-7'>Actions</h6> }
  ]

  const rows: Array<React.ReactNode[]> = []
  const rowMeta: Array<{ key?: string; className?: string }> = []

  displayInvoices.forEach((invoice: Invoice) => {
    const isUpdating = updatingInvoiceId === invoice._id
    const isRemoving = invoice._id ? removingInvoiceIds.has(invoice._id) : false

    rowMeta.push({
      key: invoice._id,
      className: isRemoving ? 'invoice-list-row--removing' : undefined,
    })

    rows.push([
      <small className='text-muted'>{formatDateShort(invoice.issueDate, user?.locale)}</small>,
      <small className='text-muted'><Link to={`/invoices/${invoice._id}`}>{invoice.code}</Link></small>,
      <small className='text-muted text-truncate'><Link to={`/clients/${invoice.client?._id}`}>{invoice.client?.tradingName}</Link></small>,
      <b className='text-primary'>
        <Amount value={invoice.total as number} currencySymbol={invoice?.businessDetails?.currency?.symbol} />
      </b>,
      <div className='text-center'>{<StatusBadge status={(invoice.status as InvoiceStatus)} />}</div>,
      <div className='d-none d-sm-block text-center'>
        <ActivityBadge activity={invoice.lastActivity} sentManually={invoice.sentManually} />
      </div>,
      <div className='d-none d-sm-flex text-center align-items-center justify-content-end gap-2 invoice-list-row-actions'>
        {invoice.status !== InvoiceStatus.PAID && (
          <Button
            icon={<CheckIcon width={20} height={20} />}
            iconPosition='start'
            variant={ButtonVariant.LIGHT}
            size={ButtonSize.SMALL}
            loading={isUpdating}
            loadingText='Updating invoice'
            onClick={() => handlePaid(invoice)}
          >
            Mark paid
          </Button>
        )}
        <InvoiceActionsDropdown
          invoice={invoice}
          onDeleted={refreshInvoices}
          disabled={isUpdating}
          buttonSize={ButtonSize.SMALL}
        />
      </div>,
    ])
  })

  if (isMobile) {
    return (
      <>
        {displayInvoices.map((invoice: Invoice) => (
          <InvoiceCard
            key={invoice._id}
            invoice={invoice}
            handlePaid={handlePaid}
            onInvoiceDeleted={refreshInvoices}
            isUpdating={updatingInvoiceId === invoice._id}
            isRemoving={invoice._id ? removingInvoiceIds.has(invoice._id) : false}
          />
        ))}
      </>
    )
  }
  return <List headers={headers} rows={rows} rowMeta={rowMeta} />
}

export default InvoiceList
